import { CheckBox, FormNotice, Spinner } from '@zappyrent/hundop';
import { upperFirst } from 'lodash';
import { ChangeEvent, useState, useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import shallow from 'zustand/shallow';

import { USERS_TYPES } from '../../../shared/constants';
import { IGuest } from '../../../shared/ts/user';
import useStore from '../../../store';
import DropDownInput from '../../___design/DropDownInput';
import TelephoneInput from '../../___design/TelephoneInput';
import TextInput from '../../___design/TextInput';
import {
  StyledButton,
  StyledDeleteIcon,
} from '../../DocumentField/DocumentField.styles';
import UploadTenantsDocuments from '../UploadTenantDocuments/UploadTenantDocuments';
import { checkForDuplicateEmails } from '../UploadTenantDocuments/utils/requiredFieldForDocumentTenant';
import {
  ButtonContainer,
  RowContainer,
  StyledButtonWrapper,
  StyledContinueButton,
  StyledFieldWrapper,
  StyledH3,
} from './AddATenant.styles';
import { IFields } from './AddATenant.types';
import {
  ID_DOCUMENT_TYPE_EN,
  ID_DOCUMENT_TYPE_IT,
  OCCUPATION_STUDENT,
} from './constants/AddATenantFields.const';
import { dateValidation } from './utils/dateValidation';
import { filterRequiredFieldForTenant } from './utils/filterRequiredFieldForTenant';

const AddATenant = ({
  index,
  guest,
  onGuaranteeChange,
  handleMissingFormRequiredFieldTenant,
  handleDeleteTenantChange,
  deleteMisingFormRequiredFieldTenant,
}: {
  index: number;
  guest: IGuest;
  onGuaranteeChange: (i_can_guarantee: boolean) => void;
  handleMissingFormRequiredFieldTenant: (
    index: number,
    missingFields: {
      label: string;
      fields: string[];
      documents: {
        label: string;
        fields: { label: string; sideLabel?: string }[];
      };
    },
  ) => void;
  handleDeleteTenantChange: (index: number) => void;
  deleteMisingFormRequiredFieldTenant: (index: number) => void;
}): JSX.Element => {
  const { locale } = useParams();
  const { t } = useTranslation([
    'common',
    'form',
    'documentsflow',
    'translation',
    'routes',
  ]);
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  const {
    guests,
    createGuestByBookingId,
    updateGuestById,
    deleteGuestById,
    guarantors,
    getGuests,
    getGuestDocuments,
  } = useStore(
    state => ({
      guests: state.guests,
      createGuestByBookingId: state.createGuestByBookingId,
      updateGuestById: state.updateGuestById,
      deleteGuestById: state.deleteGuestById,
      guarantors: state.guarantors,
      getGuests: state.getGuests,
      getGuestDocuments: state.getGuestDocuments,
    }),
    shallow,
  );
  const [formData, setFormData] = useState({
    firstName: guest?.user_first_name ?? '',
    lastName: guest?.user_last_name ?? '',
    occupation: guest?.user_occupation ?? '',
    email: guest?.user_email ?? '',
    placeOfBirth: guest?.user_place_of_birth ?? '',
    dateOfBirth: guest?.user_birth ?? '',
    identification_number: guest?.user_id_document_number ?? '',
    identification_type: guest?.user_id_document_type ?? '',
    tax_code: guest?.user_tax_code ?? '',
    monthlyIncome: guest?.user_monthly_income ?? '',
    cityOfResidence: guest?.user_residence_city ?? '',
    address: guest?.user_residence_address ?? '',
    postalCode: guest?.user_postal_code ?? '',
    province: guest?.user_residence_province ?? '',
    country: guest?.user_country ?? '',
    phoneNumber: guest?.user_phone_no ?? '',
    documentExpiryDate: guest?.user_id_document_expiry_date ?? '',
    universityName: guest?.user_university_name ?? '',
    fieldOfStudy: guest?.user_field_of_study ?? '',
  });
  const [guestId, setGuestId] = useState(guest?.id);
  const [userId, setUserId] = useState(Number(guest?.user_id));
  const [i_can_guarantee, setIncomeProofGuarantee] = useState(false);
  const [isDeleteClicked, setIsDeleteClicked] = useState(false);
  const [tenantMissingDocuments, setTenantMissingDocuments] = useState<
    { label: string; fields: { label: string; sideLabel?: string }[] }[]
  >([]);
  const [duplicateEmailError, setDuplicateEmailError] = useState(false);

  const areInputsFilled = Object.entries(formData).every(([key, value]) => {
    if (key === 'email') {
      return (
        value !== '' &&
        value !== null &&
        value !== undefined &&
        !duplicateEmailError &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      );
    }
    if (key === 'tax_code') {
      return true;
    }

    if (key === 'documentExpiryDate') {
      return dateValidation(value);
    }
    return value !== '' && value !== null && value !== undefined;
  });

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = event.target;
      if (name === 'email') {
        const error = checkForDuplicateEmails(
          value,
          index,
          'tenant',
          guests,
          guarantors,
        );
        setDuplicateEmailError(error);
      }

      setFormData(prevData => ({ ...prevData, [name]: value }));
    },
    [],
  );

  const { mutateAsync, isLoading } = useMutation(async () => {
    const existingGuest = guests.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (g: any) =>
        g.user_first_name?.toLowerCase() === formData.firstName.toLowerCase() &&
        g.user_last_name?.toLowerCase() === formData.lastName.toLowerCase() &&
        g.occupation === formData.occupation,
    );
    setGuestId(existingGuest?.id ?? guest.id);

    if (!userId) {
      const user = await createGuestByBookingId(bookingId!, {
        name: { first_name: formData.firstName, last_name: formData.lastName },
        occupation: formData.occupation,
        contacts: { email: formData.email, phone_no: formData.phoneNumber },
        place_of_birth: formData.placeOfBirth,
        date_of_birth: formData.dateOfBirth,
        id_document_number: formData.identification_number,
        id_document_type: formData.identification_type,
        id_document: {
          number: formData.identification_number,
          type: formData.identification_type,
          expiry_date: formData.documentExpiryDate,
        },
        tax_code: formData.tax_code,
        monthly_income: parseFloat(formData.monthlyIncome),
        city: formData.cityOfResidence,
        residence_address: formData.address,
        residence_postcode: formData.postalCode,
        residence_province: formData.province,
        country: formData.country,
        type: 'Tenant',
        landlord_type: 'owner',
        university_name: formData.universityName,
        field_of_study: formData.fieldOfStudy,
      });
      setUserId(Number(user.user_id));
      setGuestId(user.id);
    } else {
      await updateGuestById(bookingId!, guestId!, {
        name: { first_name: formData.firstName, last_name: formData.lastName },
        occupation: formData.occupation,
        contacts: { email: formData.email, phone_no: formData.phoneNumber },
        place_of_birth: formData.placeOfBirth,
        date_of_birth: formData.dateOfBirth,
        id_document_number: formData.identification_number,
        id_document_type: formData.identification_type,
        id_document: {
          number: formData.identification_number,
          type: formData.identification_type,
          expiry_date: formData.documentExpiryDate,
        },
        tax_code: formData.tax_code,
        monthly_income: parseFloat(formData.monthlyIncome),
        type: 'Tenant',
        city: formData.cityOfResidence,
        residence_address: formData.address,
        residence_postcode: formData.postalCode,
        residence_province: formData.province,
        country: formData.country,
        university_name: formData.universityName,
        field_of_study: formData.fieldOfStudy,
      });
      await getGuests(bookingId!);
      await Promise.all(
        guests.map(async (guest: IGuest) => {
          if (guest.id) {
            await getGuestDocuments(guest?.id ?? '', bookingId!);
          }
        }),
      );
    }
  });

  const handleConfirmClick = () => {
    if (bookingId && areInputsFilled) {
      mutateAsync();
    }
  };
  const handleConfirmGuestCanGurantee = (i_can_guarantee: boolean) => {
    setIncomeProofGuarantee(i_can_guarantee);
    onGuaranteeChange(i_can_guarantee);
  };
  const { control } = useForm();
  const isFieldEmpty = (field: keyof typeof formData) => {
    if (field === 'tax_code') {
      return false;
    }
    return !formData[field];
  };
  const emailError =
    !formData.email.includes('@') ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);

  const handleMissingTenantDocument = (
    index: number,
    missingFields: {
      label: string;
      fields: { label: string; sideLabel?: string }[];
    },
  ) => {
    setTenantMissingDocuments(prevFields => {
      const updatedFields = [...prevFields];
      updatedFields[index] = missingFields;
      return updatedFields;
    });
  };

  useEffect(() => {
    const missingValue = filterRequiredFieldForTenant({
      index,
      tenant: formData,
    });
    handleMissingFormRequiredFieldTenant(index, {
      ...missingValue,
      documents: tenantMissingDocuments[tenantMissingDocuments?.length - 1],
    });
  }, [formData, index, tenantMissingDocuments]);

  const handleOnDeleteButtonClick = async () => {
    if (userId) {
      setIsDeleteClicked(true);
      await deleteGuestById(bookingId!, userId!);
      setIsDeleteClicked(false);
    } else {
      handleDeleteTenantChange(index);
    }
    deleteMisingFormRequiredFieldTenant(index);
  };

  const fields: IFields[] = [
    {
      name: 'firstName',
      type: 'text',
      required: true,
    },
    {
      name: 'lastName',
      type: 'text',
      required: true,
    },
    {
      name: 'phoneNumber',
      type: 'tel',
      required: true,
    },
    {
      name: 'email',
      type: 'email',
      required: true,
    },
    {
      name: 'placeOfBirth',
      type: 'text',
      required: true,
    },
    {
      name: 'dateOfBirth',
      type: 'date',
      required: true,
    },
    {
      name: 'cityOfResidence',
      type: 'text',
      required: true,
    },
    {
      name: 'address',
      type: 'text',
      required: true,
    },
    {
      name: 'province',
      type: 'text',
      required: true,
    },
    {
      name: 'country',
      type: 'text',
      required: true,
    },
    {
      name: 'postalCode',
      type: 'text',
      required: true,
    },
    {
      name: 'identification_type',
      type: 'dropdown',
      options: locale === 'en' ? ID_DOCUMENT_TYPE_EN : ID_DOCUMENT_TYPE_IT,
      required: true,
    },
    {
      name: 'identification_number',
      type: 'text',
      required: true,
    },
    {
      name: 'documentExpiryDate',
      type: 'date',
      required: true,
    },
    {
      name: 'tax_code',
      type: 'text',
      required: false,
    },
    {
      name: 'monthlyIncome',
      type: 'number',
      required: true,
    },
  ];

  const showOccupationHelpertext = [
    USERS_TYPES.ITALIAN_STUDENT,
    USERS_TYPES.FOREIGN_STUDENT,
    USERS_TYPES.SELF_EMPLOYED,
    USERS_TYPES.UNEMPLOYED,
  ];

  return (
    <>
      <RowContainer>
        <StyledH3>{upperFirst(t('translation:personalInformation'))}</StyledH3>
        {index !== 0 && (
          <StyledButtonWrapper>
            {isDeleteClicked && <Spinner />}
            <StyledButton
              onClick={handleOnDeleteButtonClick}
              title={t('a11y:deleteTenant')}
            >
              <StyledDeleteIcon />
            </StyledButton>
          </StyledButtonWrapper>
        )}{' '}
      </RowContainer>
      <StyledFieldWrapper>
        {fields.map(field => {
          if (field.type === 'dropdown') {
            return (
              <DropDownInput
                required={field.required}
                id={field.name}
                control={control}
                onSelect={handleInputChange}
                name={field.name}
                label={upperFirst(t(`form:${field.name}`))}
                options={field.options}
                value={formData.identification_type}
              />
            );
          }
          if (field.type === 'tel') {
            return (
              <TelephoneInput
                id={field.name}
                label={upperFirst(t(`form:${field.name}`))}
                name={field.name}
                value={formData.phoneNumber as string}
                required={field.required}
                onChange={value => {
                  setFormData(prevData => ({
                    ...prevData,
                    [field.name]: value.split(' ').join(''),
                  }));
                }}
              >
                {isFieldEmpty(field.name as keyof typeof formData) && (
                  <FormNotice isWrapped={true}>
                    {t('form:requiredField')}
                  </FormNotice>
                )}
              </TelephoneInput>
            );
          }
          return (
            <Controller
              key={field.name}
              control={control}
              name={field.name}
              render={({ field: {} }) => (
                <TextInput
                  key={field.name}
                  id={field.name}
                  label={upperFirst(t(`form:${field.name}`))}
                  name={field.name}
                  type={
                    field.type as
                      | 'number'
                      | 'email'
                      | 'text'
                      | 'date'
                      | 'tel'
                      | 'password'
                      | undefined
                  }
                  value={
                    formData[field.name as keyof typeof formData] as string
                  }
                  onChange={handleInputChange}
                  required={field.required}
                >
                  {isFieldEmpty(field.name as keyof typeof formData) && (
                    <FormNotice isWrapped={true}>
                      {t('form:requiredField')}
                    </FormNotice>
                  )}
                  {field.name === 'email' &&
                    emailError &&
                    !isFieldEmpty(field.name as keyof typeof formData) && (
                      <FormNotice isWrapped={true}>
                        {t('form:emailIsNotValid')}
                      </FormNotice>
                    )}
                  {field.name === 'email' &&
                    duplicateEmailError &&
                    !isFieldEmpty(field.name as keyof typeof formData) && (
                      <FormNotice isWrapped={true}>
                        {t('form:duplicateEmail')}
                      </FormNotice>
                    )}
                </TextInput>
              )}
            />
          );
        })}
        <DropDownInput
          required={true}
          id="occupation"
          control={control}
          onSelect={handleInputChange}
          name="occupation"
          label={upperFirst(t('form:occupation'))}
          options={[
            {
              label: upperFirst(t('form:italianStudent')),
              value: USERS_TYPES.ITALIAN_STUDENT,
            },
            {
              label: upperFirst(t('form:foreignStudent')),
              value: USERS_TYPES.FOREIGN_STUDENT,
            },
            {
              label: upperFirst(t('form:selfEmployed')),
              value: USERS_TYPES.SELF_EMPLOYED,
            },
            {
              label: upperFirst(t('form:employee')),
              value: USERS_TYPES.EMPLOYEE,
            },
            {
              label: upperFirst(t('form:unemployed')),
              value: USERS_TYPES.UNEMPLOYED,
            },
            {
              label: upperFirst(t('form:retired')),
              value: USERS_TYPES.RETIRED,
            },
          ]}
          value={formData.occupation}
          helperText={
            showOccupationHelpertext.includes(formData.occupation)
              ? upperFirst(t('documentsflow:occupationHelperText'))
              : ''
          }
        />
        {OCCUPATION_STUDENT.includes(formData.occupation) && (
          <>
            <TextInput
              id={'universtiy_name'}
              label={upperFirst(t(`form:universtiy_name`))}
              name="universityName"
              type="text"
              value={formData.universityName as string}
              onChange={handleInputChange}
              required={true}
            />
            <TextInput
              id={'field_of_study'}
              label={upperFirst(t(`form:field_of_study`))}
              name="fieldOfStudy"
              type="text"
              value={formData.fieldOfStudy as string}
              onChange={handleInputChange}
              required={true}
            />
          </>
        )}
      </StyledFieldWrapper>
      <br />
      <CheckBox
        id={`income-proof-${guest.id ?? Math.random()}`}
        name="income-proof"
        label={t('translation:incomeProofConditions')}
        checked={i_can_guarantee}
        onChange={() => {
          handleConfirmGuestCanGurantee(!i_can_guarantee);
        }}
      />
      <ButtonContainer>
        {isLoading ? (
          <Spinner />
        ) : (
          <StyledContinueButton
            disabled={!areInputsFilled}
            onClick={handleConfirmClick}
            title={t('common:confirm')}
          >
            {t('common:confirm')}
          </StyledContinueButton>
        )}
      </ButtonContainer>
      {userId !== null && guestId && (
        <UploadTenantsDocuments
          index={index}
          user_id={Number(userId)}
          guest_id={guestId!}
          occupation={formData.occupation ?? guest.user_occupation}
          guest={guest}
          handleMissingTenantDocument={handleMissingTenantDocument}
        />
      )}
    </>
  );
};

export default AddATenant;
